import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, ClockIcon, ArrowPathIcon, CircleStackIcon } from '@heroicons/react/24/outline'
import { colorMap } from '../../../Common'

// Opções permitidas pelo backend (certs.configuracaoAreasFilas.intervalo_tempos)
const TIPO_OPTIONS = [
    { value: 'hours', label: 'Horas' },
    { value: 'days', label: 'Dias' },
]
const MIN_INTERVALO = 1

const ModalConfiguracaoBusca = ({ open, onClose, config, onSave, color = 'yellow' }) => {
    const colorClasses = colorMap[color] || colorMap.yellow

    const [tipoIntervalo, setTipoIntervalo] = useState('hours')
    const [intervalo, setIntervalo] = useState('')
    const [errorTipo, setErrorTipo] = useState(false)
    const [errorIntervalo, setErrorIntervalo] = useState(false)
    const [errorIntervaloMsg, setErrorIntervaloMsg] = useState('Campo obrigatório')

    useEffect(() => {
        if (open && config) {
            setTipoIntervalo(config.tipo_intervalo_tempo || 'hours')
            setIntervalo(config.intervalo_tempo ?? '')
            setErrorTipo(false)
            setErrorIntervalo(false)
        }
        if (!open) {
            setTipoIntervalo('hours')
            setIntervalo('')
            setErrorTipo(false)
            setErrorIntervalo(false)
            setErrorIntervaloMsg('Campo obrigatório')
        }
    }, [open, config])

    const handleSubmit = () => {
        let error = false

        if (!TIPO_OPTIONS.some((o) => o.value === tipoIntervalo)) {
            setErrorTipo(true)
            error = true
        } else setErrorTipo(false)

        const numero = Number(intervalo)
        if (intervalo === '' || intervalo === null) {
            setErrorIntervalo(true)
            setErrorIntervaloMsg('Campo obrigatório')
            error = true
        } else if (!Number.isInteger(numero) || numero < MIN_INTERVALO) {
            setErrorIntervalo(true)
            setErrorIntervaloMsg(`Deve ser um número inteiro maior ou igual a ${MIN_INTERVALO}`)
            error = true
        } else setErrorIntervalo(false)

        if (error) return

        onSave({
            tipo_intervalo_tempo: tipoIntervalo,
            intervalo_tempo: numero,
        })
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-full ${colorClasses.headerIconBg} mr-4`}>
                                            <CircleStackIcon className={`h-6 w-6 ${colorClasses.headerIconText}`} />
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                                                Editar configuração
                                            </Dialog.Title>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {config?.area ? `Área: ${config.area}` : 'Ajuste o intervalo de busca'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label htmlFor="tipo_intervalo_tempo" className="block text-sm font-semibold text-gray-700 mb-2">
                                            <div className="flex items-center">
                                                <ArrowPathIcon className="w-4 h-4 mr-2 text-gray-500" />
                                                Tipo de intervalo
                                            </div>
                                        </label>
                                        <select
                                            id="tipo_intervalo_tempo"
                                            value={tipoIntervalo}
                                            onChange={(e) => { setTipoIntervalo(e.target.value); setErrorTipo(false) }}
                                            className={`mt-1 block w-full px-4 py-3 rounded-xl border ${errorTipo
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : `${colorClasses.inputBorder} focus:ring-brand-yellow focus:border-brand-yellow`
                                                } bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2`}
                                        >
                                            {TIPO_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        {errorTipo && (
                                            <p className="mt-2 text-sm text-red-600 flex items-center">
                                                <span className="mr-1">⚠</span>
                                                Selecione um tipo de intervalo válido
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="intervalo_tempo" className="block text-sm font-semibold text-gray-700 mb-2">
                                            <div className="flex items-center">
                                                <ClockIcon className="w-4 h-4 mr-2 text-gray-500" />
                                                Intervalo de tempo
                                            </div>
                                        </label>
                                        <input
                                            type="number"
                                            id="intervalo_tempo"
                                            min={MIN_INTERVALO}
                                            step={1}
                                            value={intervalo}
                                            onChange={(e) => { setIntervalo(e.target.value); setErrorIntervalo(false) }}
                                            className={`mt-1 block w-full px-4 py-3 rounded-xl border ${errorIntervalo
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : `${colorClasses.inputBorder} focus:ring-brand-yellow focus:border-brand-yellow`
                                                } bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2`}
                                            placeholder="Ex.: 2"
                                        />
                                        <p className="mt-2 text-xs text-gray-500">
                                            A busca será executada a cada {intervalo || '—'} {tipoIntervalo === 'hours' ? 'hora(s)' : 'dia(s)'}.
                                        </p>
                                        {errorIntervalo && (
                                            <p className="mt-2 text-sm text-red-600 flex items-center">
                                                <span className="mr-1">⚠</span>
                                                {errorIntervaloMsg}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-8 flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-200"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className={`px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r ${colorClasses.buttonPrimary} ${colorClasses.buttonPrimaryHover} shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105`}
                                    >
                                        Salvar Alterações
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalConfiguracaoBusca
